import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { ComparisonFeature } from '../types';

interface ComparisonMatrixProps {
  features: ComparisonFeature[];
}

const ComparisonMatrix = ({ features }: ComparisonMatrixProps) => {
  const renderValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <div className="flex justify-center">
          <div className="bg-success/10 rounded-full p-1">
            <Icon name="Check" size={16} className="text-success" />
          </div>
        </div>
      ) : (
        <div className="flex justify-center">
           <Icon name="Minus" size={16} className="text-muted-foreground/30" />
        </div>
      );
    }
    return <span className="text-sm font-medium text-foreground">{value}</span>;
  };

  return (
    <section className="py-20 bg-muted/30 border-y border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl font-bold text-foreground">
            Compare Service Packages
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Find the perfect level of automation for your current stage of growth.
          </p>
        </div>

        <div className="bg-card rounded-2xl border border-border shadow-xl overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="text-left p-6 w-1/4">
                    <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Features</span>
                  </th>
                  <th className="p-6 w-1/4 text-center">
                    <div className="text-lg font-bold text-foreground">Starter</div>
                    <div className="text-sm text-muted-foreground mt-1">For small teams</div>
                  </th>
                  <th className="p-6 w-1/4 text-center bg-primary/5 relative">
                    <div className="absolute top-0 left-0 w-full h-1 bg-primary"></div>
                    <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 px-2 py-0.5 rounded-full mb-2">
                      <Icon name="Star" size={10} /> Popular
                    </div>
                    <div className="text-lg font-bold text-primary">Professional</div>
                    <div className="text-sm text-muted-foreground mt-1">For scaling agencies</div>
                  </th>
                  <th className="p-6 w-1/4 text-center">
                    <div className="text-lg font-bold text-foreground">Enterprise</div>
                    <div className="text-sm text-muted-foreground mt-1">For large orgs</div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {features.map((feature, index) => (
                  <tr key={index} className="hover:bg-muted/50 transition-colors">
                    <td className="p-6 text-sm font-medium text-muted-foreground">
                      {feature.feature}
                    </td>
                    <td className="p-6 text-center">{renderValue(feature.starter)}</td>
                    <td className="p-6 text-center bg-primary/5 font-medium">{renderValue(feature.professional)}</td>
                    <td className="p-6 text-center">{renderValue(feature.enterprise)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/20">
                  <td className="p-6"></td>
                  <td className="p-6 text-center">
                    <Link to="/contact">
                      <Button variant="outline" size="sm" fullWidth>Select Starter</Button>
                    </Link>
                  </td>
                  <td className="p-6 text-center bg-primary/5">
                    <Link to="/contact">
                      <Button variant="default" size="sm" fullWidth>Select Professional</Button>
                    </Link>
                  </td>
                  <td className="p-6 text-center">
                    <Link to="/contact">
                      <Button variant="outline" size="sm" fullWidth>Contact Sales</Button>
                    </Link>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonMatrix;